import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CARD_BG = '#1f2937';   // dunkelgrau (passt zu euren Cards)
const TEXT = '#e5e7eb';      // hellgrau
const SUBTLE = '#9ca3af';    // dezenter Text
const FIELD_BG = '#111827';  // sehr dunkles Feld
const GREEN = '#8bff9a';     // Mentixx/MENVIO-Grün
const GREEN_DARK = '#68e07a';
const BORDER = 'rgba(255,255,255,0.08)';

export default function ContactPage() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();

  const isEn = String(i18n.language || '').toLowerCase().startsWith('en');
  const tf = (key, de, en) => t(key, { defaultValue: isEn ? en : de });

  // Optionen (stabile Values, Labels lokalisiert)
  const topicOptions = useMemo(
    () => [
      { value: 'live_demo', label: tf('contact.topic.liveDemo', 'Live-Demo anfragen', 'Request a live demo') },
      { value: 'license_offer', label: tf('contact.topic.licenseOffer', 'Lizenz / Angebot', 'License / quote') },
      { value: 'tech_question', label: tf('contact.topic.techQuestion', 'Technische Frage', 'Technical question') },
      { value: 'partner_press', label: tf('contact.topic.partnerPress', 'Partnerschaft / Presse', 'Partnership / press') },
      { value: 'other', label: tf('contact.topic.other', 'Sonstiges', 'Other') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  const roleOptions = useMemo(
    () => [
      { value: 'hr', label: tf('contact.role.hr', 'HR', 'HR') },
      { value: 'mentee', label: tf('contact.role.mentee', 'Mentee', 'Mentee') },
      { value: 'mentor', label: tf('contact.role.mentor', 'Mentor', 'Mentor') },
      { value: 'leader', label: tf('contact.role.leader', 'Leader', 'Leader') },
      { value: 'other', label: tf('contact.role.other', 'Sonstiges', 'Other') },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  // Formularzustand
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: 'MENVIO GmbH',
    role: 'hr',
    topic: 'live_demo',
    message: '',
    consent: true,
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const canSubmit =
    form.name.trim() &&
    form.email.trim() &&
    form.message.trim() &&
    form.consent;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const to = 'hello@menvio.ai';

    const roleLabel = roleOptions.find((r) => r.value === form.role)?.label || form.role;
    const topicLabel = topicOptions.find((o) => o.value === form.topic)?.label || form.topic;

    const subject = `[${roleLabel}] ${topicLabel} – ${form.name}`;

    const body = [
      `${tf('contact.mail.name', 'Name', 'Name')}: ${form.name}`,
      `${tf('contact.mail.email', 'E-Mail', 'Email')}: ${form.email}`,
      `${tf('contact.mail.company', 'Firma', 'Company')}: ${form.company || '—'}`,
      `${tf('contact.mail.role', 'Rolle', 'Role')}: ${roleLabel}`,
      `${tf('contact.mail.topic', 'Thema', 'Topic')}: ${topicLabel}`,
      '',
      tf('contact.mail.messageHeader', 'Nachricht:', 'Message:'),
      form.message,
    ].join('\n');

    const url = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        color: TEXT,
        background: '#0b0b0b',
      }}
    >
      {/* Kopfbereich mit Zurück-Button */}
      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: '24px 16px 8px',
        }}
      >
        <button
          type="button"
          onClick={() => nav(-1)}
          title={tf('contact.backTitle', 'Zurück', 'Back')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'transparent',
            color: TEXT,
            border: `1px solid ${BORDER}`,
            borderRadius: 10,
            padding: '8px 12px',
            cursor: 'pointer',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>
            ←
          </span>
          <span>{tf('contact.back', 'Zurück', 'Back')}</span>
        </button>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '10px 16px 28px' }}>
        <h1 style={{ fontSize: 44, margin: '8px 0 8px', fontWeight: 800 }}>
          {tf('contact.title', 'Kontaktiere uns', 'Contact us')}
        </h1>
        <p style={{ margin: 0, color: SUBTLE, fontSize: 16 }}>
          {tf(
            'contact.subtitle',
            'Wir unterstützen dich gern bei Demo, Angebot, Fragen oder Support.',
            'We’re happy to help with a demo, a quote, questions, or support.'
          )}
        </p>
      </div>

      {/* Layout: Form + Side Card */}
      <div
        style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: '0 16px 60px',
          display: 'grid',
          gridTemplateColumns: '1.3fr 0.7fr',
          gap: 18,
        }}
      >
        {/* Form Card */}
        <div
          style={{
            background: CARD_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 16,
            padding: 18,
            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
          }}
        >
          <form onSubmit={handleSubmit}>
            {/* Grid Inputs */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
              }}
            >
              <div>
                <label style={{ display: 'block', fontSize: 13, color: SUBTLE, marginBottom: 6 }}>
                  {tf('contact.labels.name', 'Name', 'Name')}
                </label>
                <input
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                  placeholder={tf('contact.placeholders.name', 'Vor- & Nachname', 'First & last name')}
                  style={{
                    width: '100%',
                    background: FIELD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: '10px 12px',
                    color: TEXT,
                    outline: 'none',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, color: SUBTLE, marginBottom: 6 }}>
                  {tf('contact.labels.email', 'E-Mail', 'Email')}
                </label>
                <input
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  placeholder={tf('contact.placeholders.email', 'name@firma.de', 'name@company.com')}
                  style={{
                    width: '100%',
                    background: FIELD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: '10px 12px',
                    color: TEXT,
                    outline: 'none',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, color: SUBTLE, marginBottom: 6 }}>
                  {tf('contact.labels.company', 'Firma (optional)', 'Company (optional)')}
                </label>
                <input
                  value={form.company}
                  onChange={(e) => update('company', e.target.value)}
                  placeholder={tf('contact.placeholders.company', 'Firma', 'Company')}
                  style={{
                    width: '100%',
                    background: FIELD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: '10px 12px',
                    color: TEXT,
                    outline: 'none',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, color: SUBTLE, marginBottom: 6 }}>
                  {tf('contact.labels.role', 'Rolle', 'Role')}
                </label>
                <select
                  value={form.role}
                  onChange={(e) => update('role', e.target.value)}
                  style={{
                    width: '100%',
                    background: FIELD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: '10px 12px',
                    color: TEXT,
                    outline: 'none',
                  }}
                >
                  {roleOptions.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 13, color: SUBTLE, marginBottom: 6 }}>
                  {tf('contact.labels.topic', 'Thema', 'Topic')}
                </label>
                <select
                  value={form.topic}
                  onChange={(e) => update('topic', e.target.value)}
                  style={{
                    width: '100%',
                    background: FIELD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: '10px 12px',
                    color: TEXT,
                    outline: 'none',
                  }}
                >
                  {topicOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 13, color: SUBTLE, marginBottom: 6 }}>
                  {tf('contact.labels.message', 'Nachricht', 'Message')}
                </label>
                <textarea
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  placeholder={tf(
                    'contact.placeholders.message',
                    'Worum geht es? Gern mit ein paar Stichpunkten…',
                    'What’s this about? A few bullet points are fine…'
                  )}
                  rows={7}
                  style={{
                    width: '100%',
                    background: FIELD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 12,
                    padding: '10px 12px',
                    color: TEXT,
                    outline: 'none',
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>

            {/* Consent */}
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={form.consent}
                onChange={(e) => update('consent', e.target.checked)}
                style={{ width: 16, height: 16, accentColor: GREEN }}
              />
              <span style={{ color: SUBTLE, fontSize: 13 }}>
                {tf(
                  'contact.consent',
                  'Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage verarbeitet werden.',
                  'I agree that my details may be processed to handle my request.'
                )}
              </span>
            </div>

            {/* Buttons */}
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  background: canSubmit ? GREEN : 'rgba(139,255,154,0.25)',
                  color: '#0b0b0b',
                  border: 'none',
                  borderRadius: 12,
                  padding: '10px 14px',
                  fontWeight: 800,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {tf('contact.buttons.send', 'Nachricht senden', 'Send message')}
              </button>

              <button
                type="button"
                onClick={() =>
                  setForm((s) => ({
                    ...s,
                    name: '',
                    email: '',
                    message: '',
                  }))
                }
                style={{
                  background: 'transparent',
                  color: TEXT,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 12,
                  padding: '10px 14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {tf('contact.buttons.clear', 'Leeren', 'Clear')}
              </button>
            </div>

            <div style={{ marginTop: 10, color: SUBTLE, fontSize: 12 }}>
              {tf(
                'contact.hint',
                'Hinweis: Beim Absenden öffnet sich dein E-Mail-Client (mailto).',
                'Note: Submitting will open your email client (mailto).'
              )}
            </div>
          </form>
        </div>

        {/* Side Card */}
        <div
          style={{
            background: CARD_BG,
            border: `1px solid ${BORDER}`,
            borderRadius: 16,
            padding: 18,
            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
            height: 'fit-content',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>
            {tf('contact.quick.title', 'Schnellzugriff', 'Quick access')}
          </h3>
          <p style={{ margin: '8px 0 14px', color: SUBTLE, fontSize: 13 }}>
            {tf('contact.quick.subtitle', 'Direkter Draht – ganz wie du magst.', 'Direct line — just how you like it.')}
          </p>

          <button
            type="button"
            onClick={() => (window.location.href = 'mailto:hello@menvio.ai')}
            style={{
              width: '100%',
              background: FIELD_BG,
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              padding: '10px 12px',
              color: TEXT,
              fontWeight: 800,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            hello@menvio.ai
          </button>

          <button
            type="button"
            onClick={() => window.open('https://cal.com/menvio', '_blank')}
            style={{
              width: '100%',
              marginTop: 10,
              background: 'transparent',
              border: `1px solid ${BORDER}`,
              borderRadius: 12,
              padding: '10px 12px',
              color: TEXT,
              fontWeight: 800,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {tf('contact.quick.bookDemo', 'Live-Demo buchen', 'Book a live demo')}
          </button>

          <div style={{ marginTop: 18, borderTop: `1px solid ${BORDER}`, paddingTop: 14 }}>
            <div style={{ color: TEXT, fontWeight: 800, fontSize: 14 }}>
              ▸ {tf('contact.quick.faq', 'FAQ', 'FAQ')}
            </div>
          </div>
        </div>
      </div>

      {/* Footer spacing */}
      <div style={{ height: 30 }} />
    </div>
  );
}
